// Service pour gérer Google Analytics
import AuditService from './auditService'

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

export class AnalyticsService {
  private static isInitialized = false;
  private static measurementId = 'G-F4FDBCMQRZ';

  // Initialiser Google Analytics
  static init(): void {
    if (typeof window !== 'undefined' && window.gtag && !this.isInitialized) {
      window.gtag('config', this.measurementId, {
        page_title: document.title,
        page_location: window.location.href,
      });
      this.isInitialized = true;
      console.log('Google Analytics initialisé');
    }
  }

  // Tracker une page vue
  static trackPageView(pagePath: string, pageTitle?: string): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', this.measurementId, {
        page_path: pagePath,
        page_title: pageTitle || document.title,
      });
    }
  }

  // Tracker un événement personnalisé
  static trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'engagement',
        event_label: parameters?.label,
        value: parameters?.value,
        ...parameters,
      });
    }
  }

  // Associer un utilisateur connecté
  static setUserId(userId: string): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', this.measurementId, {
        user_id: userId,
      });
    }
  }

  // Tracker les connexions
  static trackLogin(method: string = 'email'): void {
    this.trackEvent('login', {
      method: method,
      event_category: 'authentication',
    });
  }

  // Tracker les inscriptions
  static trackSignUp(method: string = 'email'): void {
    this.trackEvent('sign_up', {
      method: method,
      event_category: 'authentication',
    });
  }

  // Tracker les recherches de parking
  static trackParkingSearch(location?: string): void {
    this.trackEvent('search', {
      search_term: location || 'unknown',
      event_category: 'parking',
      event_label: 'parking_search',
    });
  }

  // Tracker l'utilisation de la carte
  static trackMapInteraction(action: string): void {
    this.trackEvent('map_interaction', {
      event_category: 'map',
      event_label: action,
    });
  }

  // Tracker les souscriptions
  static trackSubscription(planType: string): void {
    this.trackEvent('purchase', {
      event_category: 'subscription',
      event_label: planType,
      currency: 'EUR',
    });
  }

  // ========== MÉTHODES D'AUDIT POUR CHARGEBACKS ==========

  // Tracker l'accès aux fonctionnalités premium
  static async trackPremiumAccess(userId: string, feature: string, details?: any): Promise<void> {
    this.trackEvent('premium_access', {
      event_category: 'premium',
      event_label: feature,
    });

    // Log audit pour chargeback protection
    await AuditService.logUserActivity({
      userId,
      eventType: 'VIEW_PREMIUM',
      details: { feature, ...details }
    });
  }

  // Tracker la réservation d'une place
  static async trackParkingBooking(userId: string, spotDetails: any): Promise<void> {
    this.trackEvent('parking_booked', {
      event_category: 'parking',
      event_label: 'spot_booked',
    });

    // Log audit pour chargeback protection
    await AuditService.logUserActivity({
      userId,
      eventType: 'BOOK_SPOT',
      details: spotDetails
    });
  }

  // Tracker le téléchargement d'un ticket
  static async trackTicketDownload(userId: string, ticketDetails: any): Promise<void> {
    this.trackEvent('ticket_downloaded', {
      event_category: 'parking',
      event_label: 'ticket_download',
    });

    // Log audit pour chargeback protection
    await AuditService.logUserActivity({
      userId,
      eventType: 'DOWNLOAD_TICKET',
      details: ticketDetails
    });
  }

  // Tracker la confirmation d'arrivée
  static async trackArrivalConfirmation(userId: string, spotDetails: any): Promise<void> {
    this.trackEvent('arrival_confirmed', {
      event_category: 'parking',
      event_label: 'arrival_confirmation',
    });

    // Log audit pour chargeback protection
    await AuditService.logUserActivity({
      userId,
      eventType: 'CONFIRM_ARRIVAL',
      details: spotDetails
    });
  }

  // Tracker l'accès au dashboard premium
  static async trackDashboardAccess(userId: string): Promise<void> {
    this.trackEvent('dashboard_access', {
      event_category: 'premium',
      event_label: 'dashboard_view',
    });

    // Log audit pour chargeback protection
    await AuditService.logUserActivity({
      userId,
      eventType: 'VIEW_DASHBOARD',
      details: { page: 'dashboard' }
    });
  }

  // Tracker l'utilisation de la carte premium
  static async trackPremiumMapUsage(userId: string, action: string, details?: any): Promise<void> {
    this.trackMapInteraction(action);

    // Log audit pour chargeback protection si c'est un usage premium
    if (action.includes('premium') || action.includes('filter') || action.includes('search')) {
      await AuditService.logUserActivity({
        userId,
        eventType: 'USE_PREMIUM_MAP',
        details: { action, ...details }
      });
    }
  }

  // Tracker les erreurs
  static trackError(errorType: string, errorMessage?: string): void {
    this.trackEvent('exception', {
      description: errorMessage || errorType,
      fatal: false,
      event_category: 'error',
    });
  }

  // Désactiver le tracking (pour le respect de la vie privée)
  static disableTracking(): void {
    if (typeof window !== 'undefined') {
      (window as any)[`ga-disable-${this.measurementId}`] = true;
    }
  }

  // Réactiver le tracking
  static enableTracking(): void {
    if (typeof window !== 'undefined') {
      (window as any)[`ga-disable-${this.measurementId}`] = false;
    }
  }
}

export default AnalyticsService;
